import React, { forwardRef, memo, type ReactNode } from 'react';
import {
  Image,
  ImageRequireSource,
  StyleSheet,
  View,
  type ImageProps as RNImageProps,
  type LayoutChangeEvent,
} from 'react-native';

/**
 * Web stub for `@d11/react-native-fast-image`.
 * The real package pulls native codegen; Metro resolves this file for `platform === 'web'`.
 */

export type ResizeMode = 'contain' | 'cover' | 'stretch' | 'center';
export type Priority = 'low' | 'normal' | 'high';
export type Transition = 'fade' | 'none';

export type Source = {
  uri?: string;
  headers?: { [key: string]: string };
  priority?: Priority;
  cache?: 'immutable' | 'web' | 'cacheOnly';
};

export interface OnLoadEvent {
  nativeEvent: {
    width: number;
    height: number;
  };
}

export interface OnProgressEvent {
  nativeEvent: {
    loaded: number;
    total: number;
  };
}

export interface OnErrorEvent {
  nativeEvent: {
    error: string;
  };
}

export type ImageStyle = RNImageProps['style'];

type RNImagePropsWithoutConflicts = Omit<
  RNImageProps,
  'source' | 'style' | 'resizeMode' | 'onLoad' | 'onError' | 'defaultSource'
>;

export interface FastImageProps extends RNImagePropsWithoutConflicts {
  source?: Source | ImageRequireSource;
  defaultSource?: ImageRequireSource;
  resizeMode?: ResizeMode;
  fallback?: boolean;
  transition?: Transition;
  children?: ReactNode;
  onLoadStart?(): void;
  onProgress?(event: OnProgressEvent): void;
  onLoad?(event: OnLoadEvent): void;
  onError?(event: OnErrorEvent): void;
  onLoadEnd?(): void;
  onLayout?: (event: LayoutChangeEvent) => void;
  style?: ImageStyle;
}

function normalizeSource(
  source: FastImageProps['source'],
): ImageRequireSource | { uri: string } | undefined {
  if (source == null) return undefined;
  if (typeof source === 'number') return source;
  if (typeof source === 'object' && 'uri' in source && typeof source.uri === 'string') {
    return { uri: source.uri };
  }
  return undefined;
}

const FastImageInner = memo(
  ({
    source,
    defaultSource,
    style,
    resizeMode = 'cover',
    onLoadStart,
    onProgress: _onProgress,
    onLoad,
    onError,
    onLoadEnd,
    fallback,
    transition: _transition,
    forwardedRef,
    tintColor,
    blurRadius,
    children: _children,
    ...rest
  }: FastImageProps & { forwardedRef: React.Ref<View> }) => {
    const resolved = normalizeSource(source);

    const handleLoad: RNImageProps['onLoad'] = e => {
      const w = e.nativeEvent.source?.width ?? 0;
      const h = e.nativeEvent.source?.height ?? 0;
      onLoad?.({ nativeEvent: { width: w, height: h } });
    };

    const handleError: RNImageProps['onError'] = e => {
      const err = e.nativeEvent.error;
      const message = typeof err === 'string' ? err : 'Failed to load image';
      onError?.({ nativeEvent: { error: message } });
    };

    if (fallback) {
      const assetSource =
        typeof resolved === 'number' ? Image.resolveAssetSource(resolved) : resolved;
      return (
        <View style={[styles.imageContainer, style]} ref={forwardedRef}>
          <Image
            {...rest}
            style={[StyleSheet.absoluteFill, tintColor != null ? { tintColor } : null]}
            source={assetSource}
            defaultSource={defaultSource}
            onLoadStart={onLoadStart}
            onLoad={handleLoad}
            onError={handleError}
            onLoadEnd={onLoadEnd}
            resizeMode={resizeMode}
            blurRadius={blurRadius}
          />
        </View>
      );
    }

    return (
      <View style={[styles.imageContainer, style]} ref={forwardedRef}>
        <Image
          {...rest}
          style={StyleSheet.absoluteFill}
          source={resolved}
          defaultSource={defaultSource}
          onLoadStart={onLoadStart}
          onLoad={handleLoad}
          onError={handleError}
          onLoadEnd={onLoadEnd}
          resizeMode={resizeMode}
          tintColor={tintColor}
          blurRadius={blurRadius}
        />
      </View>
    );
  },
);

const FastImageComponent = forwardRef<View, FastImageProps>((props, ref) => (
  <FastImageInner {...props} forwardedRef={ref} />
));

FastImageComponent.displayName = 'FastImage';

const resizeMode = {
  contain: 'contain',
  cover: 'cover',
  stretch: 'stretch',
  center: 'center',
} as const;

const cacheControl = {
  immutable: 'immutable',
  web: 'web',
  cacheOnly: 'cacheOnly',
} as const;

const priority = {
  low: 'low',
  normal: 'normal',
  high: 'high',
} as const;

const transition = {
  fade: 'fade',
  none: 'none',
} as const;

const FastImage = FastImageComponent as typeof FastImageComponent & {
  resizeMode: typeof resizeMode;
  priority: typeof priority;
  cacheControl: typeof cacheControl;
  transition: typeof transition;
  preload: (sources: Source[]) => void;
  clearMemoryCache: () => Promise<void>;
  clearDiskCache: () => Promise<void>;
};

FastImage.resizeMode = resizeMode;
FastImage.cacheControl = cacheControl;
FastImage.priority = priority;
FastImage.transition = transition;

FastImage.preload = (sources: Source[]) => {
  Promise.all(
    sources.map(s => {
      if (s?.uri) return Image.prefetch(s.uri);
      return Promise.resolve();
    }),
  ).catch(() => {});
};

FastImage.clearMemoryCache = () => Promise.resolve();
FastImage.clearDiskCache = () => Promise.resolve();

const styles = StyleSheet.create({
  imageContainer: {
    overflow: 'hidden',
  },
});

export default FastImage;
